import { Flex } from '@uhg-abyss/web/ui/Flex';
import { Link } from '@uhg-abyss/web/ui/Link';
import { Text } from '@uhg-abyss/web/ui/Text';
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { PortalContext } from '../../../../context/PortalContext';
import { useCoverageType } from '../../../../hooks/useCoverageType';
import { useCurrentMember } from '../../../../hooks/useCurrentMember';
import { formatListWithOr } from '../../../../utils/string.utils';
import { parseLOB } from '../../../../utils/user.utils';
import { LineOfBusiness } from '../../../Constants';

const SupportedTypeaheadCoverageTypes = {
  M: 'Medical',
  D: 'Dental',
  V: 'Vision',
  B: 'Behavioral Health',
};

export interface GuestDropdownBeforeMessageProps {
  show?: boolean;
}

export const GuestDropdownBeforeMessage: React.FC<
  GuestDropdownBeforeMessageProps
> = ({ show }) => {
  const coverageType = useCoverageType() || [];
  const currentMember = useCurrentMember();
  const selectedCoverage = coverageType[0];
  const { portalData } = React.useContext(PortalContext);
  const isGuestUser = portalData
    ? portalData?.portalConfig?.isGuestUser
    : false;

  const parsedLob = parseLOB(
    currentMember?.lineOfBusiness || LineOfBusiness.EI
  );
  const url = window.location.href;

  const { t } = useTranslation();
  const isGuestWithPlan = url.includes('guest-with-plan');

  const otherCoverageTypesString = useMemo(() => {
    const otherCoverageTypes = Object.keys(
      SupportedTypeaheadCoverageTypes
    ).filter((key) => key !== selectedCoverage);

    const coverages = otherCoverageTypes.map((key) =>
      t(
        SupportedTypeaheadCoverageTypes[
          key as keyof typeof SupportedTypeaheadCoverageTypes
        ]
      )
    );

    return formatListWithOr(coverages, String(t('or')));
  }, [coverageType, t]);

  const navigateToPlanSelection = () => {
    if (isGuestWithPlan) {
      const demoHeaderEvent = new CustomEvent('demoHeaderListener', {
        bubbles: true,
      });
      window.dispatchEvent(demoHeaderEvent);
      return;
    }
    const navigationEvent = new CustomEvent('guestNavigationRequest', {
      detail: {
        path: '/select-coverage-type',
        params: { planSelectionLob: parsedLob },
      },
      bubbles: true,
    });
    window.dispatchEvent(navigationEvent);
  };

  const authenticatedUserMessage = t('TYPEAHEAD_SUGGESTION_BEFORE_MESSAGE')
    .replace(
      '{selectedCoverage}',
      t(
        SupportedTypeaheadCoverageTypes[
          selectedCoverage as keyof typeof SupportedTypeaheadCoverageTypes
        ]
      ).toLowerCase()
    )
    .replace('{otherCoverages}', otherCoverageTypesString.toLowerCase());

  const guestMessage = t(
    'TYPEAHEAD_SUGGESTION_BEFORE_MESSAGE_NO_CHANGE'
  ).replace(
    '{selectedCoverage}',
    t(
      SupportedTypeaheadCoverageTypes[
        selectedCoverage as keyof typeof SupportedTypeaheadCoverageTypes
      ]
    ).toLowerCase()
  );

  const message = isGuestUser ? guestMessage : authenticatedUserMessage;

  if (!show) return null;

  if (!coverageType?.length) {
    // no coverage set
    return null;
  }

  return (
    <Flex alignItems="center">
      <Text
        aria-label={message}
        color="$core.color.neutral.80"
        css={{
          fontSize: '$sm',
          '@media (min-width: 745px)': {
            fontSize: '$md',
          },
          marginRight: '4px',
        }}
        data-testid="guest-dropdown-before-message"
      >
        {message}
      </Text>

      {!isGuestUser && (
        <Link
          data-testid="button-click-here"
          fontWeight="bold"
          onClick={navigateToPlanSelection}
        >
          {t('CLICK_HERE').toLowerCase()}
        </Link>
      )}
    </Flex>
  );
};

export default GuestDropdownBeforeMessage;
